import { http, request } from './httpClient';
import type {
  Account,
  Analysis,
  AnalysisPeriod,
  BarcodeProduct,
  BodyMeasurement,
  DaySummary,
  Goal,
  GoalProposal,
  GoalType,
  Macros,
  Meal,
  MealDefaults,
  MealType,
  MeasurementUnit,
  Product,
  ProductSearchResult,
  Profile,
  Sex,
} from './types';

/** Lo que el cliente declara de un ítem (RF-09). Los cuatro campos son opcionales por separado. */
export interface MealItemInput {
  freeText?: string | null;
  ean?: string | null;
  quantity?: number | null;
  unit?: MeasurementUnit | null;
}

export interface CreateMealInput {
  occurredAt?: string | null;
  type?: MealType | null;
  note?: string | null;
  items: MealItemInput[];
  /** Identidad que genera el cliente para que un reintento no duplique la comida (RF-49). */
  clientMealId?: string | null;
}

export interface LabelValuesInput {
  kcal: number;
  proteinG: number;
  carbohydratesG: number;
  fatG: number;
  fiberG?: number | null;
  sodiumMg?: number | null;
  perServing: boolean;
  servingWeightG?: number | null;
  /** Sube la confianza a 1,00 cuando la persona tocó algún campo (CU-04 paso 6). */
  wasCorrected?: boolean;
  /** RF-52 · además de resolver el ítem, sumar la lectura al catálogo compartido. */
  publishToCatalog?: boolean;
  productName?: string | null;
  productBrand?: string | null;
}

/** CU-12 A2 paso 1 · lo leído, y dónde quedó la foto de la que se leyó. */
export interface LabelPhotoResult {
  /**
   * Identificador interno de la imagen. No es una URL y no sirve para verla: se reenvía tal cual
   * en el paso 2, que es lo que ata el producto a la foto de la que salieron sus números.
   */
  storageKey: string;
  extraction: LabelExtraction;
}

/** CU-12 A2 paso 2 · lo que se publica al catálogo compartido. */
export interface PublishFromLabelInput {
  name: string;
  brand?: string | null;
  ean?: string | null;
  description?: string | null;
  declaredKcal: number;
  macros100g: Macros;
  servingWeightG?: number | null;
  labelStorageKey?: string | null;
  photoStorageKey?: string | null;
}

/** Lo que vuelve del rótulo: la comida, y qué pasó con el aporte al catálogo. */
export interface SubmitLabelResult {
  meal: Meal;
  /** La fila que nació de esta lectura, si nació alguna. */
  publishedProductId: string | null;
  /** Por qué no entró, cuando se pidió publicar y no se pudo. */
  publishRejected: string | null;
}

export interface CorrectionInput {
  quantity?: number | null;
  unit?: MeasurementUnit | null;
  productId?: string | null;
  macros?: Macros | null;
}

export interface LabelExtraction {
  kcal: number | null;
  proteinG: number | null;
  carbohydratesG: number | null;
  fatG: number | null;
  fiberG: number | null;
  sodiumMg: number | null;
  perServing: boolean;
  servingWeightG: number | null;
  hasMandatoryFields: boolean;
  /** Falso cuando no hay extractor configurado: el rótulo se carga a mano (CU-04 A1). */
  extractionAvailable: boolean;
}

export interface SyncedMeal {
  clientMealId: string | null;
  accepted: boolean;
  meal: Meal | null;
  error: string | null;
}

export interface SyncResult {
  accepted: number;
  rejected: number;
  results: SyncedMeal[];
}

export interface ReprocessResult {
  affected: number;
  executed: boolean;
  changed: number;
  unchanged: number;
}

export interface AccountDeletion {
  id: string;
  deletedAt: string;
  queuedStorageObjects: number;
}

export interface SignedImageUrl {
  url: string;
  expiresAt: string;
}

export const accountsApi = {
  register: (email: string, password: string) =>
    http.post<Account>('/api/accounts', { email, password }),
  signIn: (email: string, password: string) =>
    http.post<Account>('/api/sessions', { email, password }),
  signOut: () => http.delete<void>('/api/sessions'),
  /** CU-14 · todo lo que el sistema guarda de la persona, en un JSON. */
  exportData: () => http.get<unknown>('/api/accounts/export'),
  /**
   * CU-15 · el `confirmed` viaja en el cuerpo porque el servidor rechaza la baja sin él: el
   * doble paso es parte del contrato, no una cortesía de la interfaz.
   */
  deleteAccount: () =>
    request<AccountDeletion>('/api/accounts', { method: 'DELETE', body: { confirmed: true } }),
};

export const profileApi = {
  get: () => http.get<Profile>('/api/profile'),
  update: (sex: Sex, birthDate: string, heightCm: number) =>
    http.put<Profile>('/api/profile', { sex, birthDate, heightCm }),
};

export const measurementsApi = {
  history: () => http.get<BodyMeasurement[]>('/api/body-measurements'),
  record: (input: {
    date?: string | null;
    weightKg: number;
    waistCm?: number | null;
    hipCm?: number | null;
    neckCm?: number | null;
    replaceExisting: boolean;
  }) => http.post<BodyMeasurement>('/api/body-measurements', input),
};

export const goalsApi = {
  list: () => http.get<Goal[]>('/api/goals'),
  proposal: (type: GoalType, activityFactor: number) =>
    http.get<GoalProposal>('/api/goals/proposal', { query: { type, activityFactor } }),
  create: (input: {
    type: GoalType;
    activityFactor: number;
    targetKcal?: number | null;
    macros?: { proteinG: number; carbohydratesG: number; fatG: number } | null;
    confirmBelowFloor: boolean;
  }) => http.post<Goal>('/api/goals', input),
};

export const summaryApi = {
  day: (date?: string) => http.get<DaySummary>('/api/summary/day', { query: { date } }),
};

export const mealsApi = {
  forDay: (date?: string) => http.get<Meal[]>('/api/meals', { query: { date } }),
  defaults: () => http.get<MealDefaults>('/api/meals/defaults'),
  get: (mealId: string) => http.get<Meal>(`/api/meals/${mealId}`),
  create: (input: CreateMealInput) => http.post<Meal>('/api/meals', input),
  update: (mealId: string, occurredAt: string, type: MealType, note: string | null) =>
    http.put<Meal>(`/api/meals/${mealId}`, { occurredAt, type, note }),
  remove: (mealId: string) => http.delete<void>(`/api/meals/${mealId}`),
  duplicate: (mealId: string, occurredAt?: string | null) =>
    http.post<Meal>(`/api/meals/${mealId}/duplicate`, { occurredAt: occurredAt ?? null }),
  addItem: (mealId: string, item: MealItemInput) =>
    http.post<Meal>(`/api/meals/${mealId}/items`, item),
  updateItem: (mealId: string, itemId: string, item: MealItemInput) =>
    http.put<Meal>(`/api/meals/${mealId}/items/${itemId}`, item),
  removeItem: (mealId: string, itemId: string) =>
    http.delete<Meal>(`/api/meals/${mealId}/items/${itemId}`),
  attachImage: (mealId: string, itemId: string, file: File, role: 'Dish' | 'NutritionLabel') => {
    const form = new FormData();
    form.append('file', file);
    form.append('role', role);
    return http.post<Meal>(`/api/meals/${mealId}/items/${itemId}/images`, form);
  },
  imageUrl: (mealId: string, itemId: string, mediaId: string) =>
    http.get<SignedImageUrl>(`/api/meals/${mealId}/items/${itemId}/images/${mediaId}/url`),
  removeImage: (mealId: string, itemId: string, mediaId: string) =>
    http.delete<Meal>(`/api/meals/${mealId}/items/${itemId}/images/${mediaId}`),
  resolveNow: (mealId: string, itemId: string) =>
    http.post<Meal>(`/api/meals/${mealId}/items/${itemId}/resolve`),
  labelExtraction: (mealId: string, itemId: string) =>
    http.get<LabelExtraction>(`/api/meals/${mealId}/items/${itemId}/label-extraction`),
  submitLabel: (mealId: string, itemId: string, values: LabelValuesInput) =>
    http.post<SubmitLabelResult>(`/api/meals/${mealId}/items/${itemId}/label`, values),
  confirm: (mealId: string, itemId: string) =>
    http.post<Meal>(`/api/meals/${mealId}/items/${itemId}/confirm`),
  correct: (mealId: string, itemId: string, correction: CorrectionInput) =>
    http.post<Meal>(`/api/meals/${mealId}/items/${itemId}/correct`, correction),
  sync: (meals: CreateMealInput[]) => http.post<SyncResult>('/api/meals/sync', { meals }),
  reprocess: (from: string, to: string, confirmed: boolean) =>
    http.post<ReprocessResult>('/api/meals/reprocess', { from, to, confirmed }),
};

export const productsApi = {
  search: (q: string, limit = 20) =>
    http.get<ProductSearchResult>('/api/products/search', { query: { q, limit } }),
  byBarcode: (ean: string) => http.get<BarcodeProduct>(`/api/products/by-barcode/${ean}`),

  /** CU-12 A2 paso 1 · subir la foto de un rótulo y leerla, sin comida de por medio. */
  readLabelPhoto: (file: File) => {
    const form = new FormData();
    form.append('file', file);

    return http.post<LabelPhotoResult>('/api/products/label-photo', form);
  },

  /** CU-12 A2 paso 2 · publicar al catálogo lo que se leyó. */
  publishFromLabel: (input: PublishFromLabelInput) =>
    http.post<Product>('/api/products/from-label', input),

  /** RF-52 · retirar del catálogo compartido una fila que uno mismo aportó. */
  withdraw: (productId: string) => http.delete<boolean>(`/api/products/${productId}`),
  createOwn: (input: {
    name: string;
    brand?: string | null;
    ean?: string | null;
    macros100g: Macros;
    servingWeightG?: number | null;
  }) => http.post<Product>('/api/products', input),
};

export const analysesApi = {
  get: (period: AnalysisPeriod, date?: string) =>
    http.get<Analysis>('/api/analyses', { query: { period, date } }),
  history: (period: AnalysisPeriod, limit = 10) =>
    http.get<Analysis[]>('/api/analyses/history', { query: { period, limit } }),
  regenerate: (period: AnalysisPeriod, anyDayInPeriod?: string) =>
    http.post<Analysis>('/api/analyses/regenerate', {
      period,
      anyDayInPeriod: anyDayInPeriod ?? null,
    }),
};
